/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Vimeo Player Adapter
 *
 * Wraps @vimeo/player with consistent API for unified Player class.
 * All methods return Promises for consistency.
 *
 * @typedef {import('@vimeo/player').default} VimeoPlayerLib
 */
export class VimeoPlayer {
  /**
   * @param {VimeoPlayerLib} player - Native Vimeo player instance
   */
  constructor(player) {
    this.player = player;
  }

  /**
   * Add event listener
   *
   * @param {string} event - Event name
   * @param {Function} listener - Event listener
   * @returns {void}
   */
  on(event, listener) {
    return this.player.on(event, listener);
  }

  /**
   * Remove event listener
   *
   * @param {string} event - Event name
   * @param {Function} [listener] - Event listener
   * @returns {void}
   */
  off(event, listener) {
    return this.player.off(event, listener);
  }

  /**
   * Load new video by ID
   *
   * @param {string|number} id - Video ID
   * @returns {Promise<void>}
   */
  async loadVideo(id) {
    const isMuted = await this.isMuted();

    return this.player.unload()
      .then(() => this.player.loadVideo(id))
      .then(() => !isMuted && this.unMute())
    ;
  }

  /**
   * Check if playing
   *
   * @returns {Promise<boolean>}
   */
  async isPlaying() {
    const [isPaused, isEnded] = await Promise.all([
      this.player.getPaused(),
      this.player.getEnded()
    ]);

    return !isPaused && !isEnded;
  }

  /**
   * Check if paused
   *
   * @returns {Promise<boolean>}
   */
  async isPaused() {
    return this.player.getPaused();
  }

  /**
   * Check if ended
   *
   * @returns {Promise<boolean>}
   */
  async isEnded() {
    return this.player.getEnded();
  }

  /**
   * Set autopause
   *
   * @param {boolean} autopause - Enable autopause
   * @returns {Promise<void>}
   */
  async setAutopause(autopause) {
    return this.player.setAutopause(autopause);
  }

  /**
   * Set color
   *
   * @param {string} color - Hex or rgb color
   * @returns {Promise<void>}
   */
  async setColor(color) {
    return this.player.setColor(color);
  }

  /**
   * Set loop
   *
   * @param {boolean} loop - Enable loop
   * @returns {Promise<void>}
   */
  async setLoop(loop) {
    return this.player.setLoop(loop);
  }

  /**
   * Check if muted
   *
   * @returns {Promise<boolean>}
   */
  async isMuted() {
    return this.player.getMuted();
  }

  /**
   * Set muted state
   *
   * @param {boolean} muted - Mute or unmute
   * @returns {Promise<void>}
   */
  async setMuted(muted) {
    return this.player.setMuted(muted);
  }

  /**
   * Set playback rate
   *
   * @param {number} rate - Playback rate (0.5 to 2)
   * @returns {Promise<void>}
   */
  async setPlaybackRate(rate) {
    // Scale from 0.5 to 2
    return this.player.setPlaybackRate(rate > 10 ? rate / 100 : rate);
  }

  /**
   * Set volume
   *
   * @param {number} level - Volume level (0 to 1)
   * @returns {Promise<void>}
   */
  async setVolume(level) {
    // Scale from 0 to 1
    return this.player.setVolume(level > 1 ? level / 100 : level);
  }

  /**
   * Unload player
   *
   * @returns {Promise<void>}
   */
  async unload() {
    return this.player.unload();
  }

  /**
   * Destroy player
   *
   * @returns {Promise<void>}
   */
  async destroy() {
    return this.player.destroy();
  }

  /**
   * Mute video
   *
   * @returns {Promise<void>}
   */
  async mute() {
    return this.player.setMuted(true);
  }

  /**
   * Unmute video
   *
   * @returns {Promise<void>}
   */
  async unMute() {
    return this.player.setMuted(false);
  }

  /**
   * Play video
   *
   * @returns {Promise<void>}
   */
  async play() {
    return this.player.play();
  }

  /**
   * Pause video
   *
   * @returns {Promise<void>}
   */
  async pause() {
    return this.player.pause();
  }

  /**
   * Stop video (same as pause for Vimeo)
   *
   * @returns {Promise<void>}
   */
  async stop() {
    return this.player.pause();
  }

  /**
   * Call method directly on player
   *
   * @param {string} method - Method name
   * @param {...any} args - Method arguments
   * @returns {Promise<any>}
   */
  async call(method, ...args) {
    return this.player[method](...args);
  }
}
